import { MobileSidebar } from "@/components/mobile-sidebar";
import { Header, Footer } from "@/components";
import { SidebarProvider } from "./components/ui/sidebar";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Outlet } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "./store/hooks";
import { setProductInfos } from "./store/slices/appSlice";
import { Toaster } from "@/components/ui/sonner";
import awsmobile from "./aws-exports.js";
import { setProductsLoading } from "./store/slices/appSlice";
import { setEnrichedProductList, setProductsForDisplay } from "./store/slices/appSlice";

import { Amplify } from "aws-amplify";
import {
  useCombineProducts,
  useCustomPaginatedProducts,
  useFetchBrands,
  useFetchCategories,
  useCartManagement,
} from "./hooks";
import ScrollToTop from "./components/ScrollToTop";
import { StripeProvider } from "./providers/StripeProvider";

import { initializeAuthFromLocalStorage } from "./utils/helper/initializeuserInfoFromLS";
Amplify.configure(awsmobile);

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const [fetchFlags, setFetchFlags] = useState({
    brands: false,
    categories: false,
    products: false,
  });

  const {
    brands: brandsRedux,
    categories: categoriesRedux,
    productsForDisplay: productsForDisplayRedux,
    productsLoading,
    cartItems,
  } = useAppSelector((state) => state.app);

  // Initialize cart management for automatic sync
  const { syncGuestCartToBackend, isAuthenticated, isLoading } = useCartManagement();

  useEffect(() => {
    setFetchFlags({
      brands: !brandsRedux || brandsRedux.length === 0,
      categories: !categoriesRedux || categoriesRedux.length === 0,
      products: !productsForDisplayRedux || productsForDisplayRedux.length === 0,
    });
    initializeAuthFromLocalStorage(dispatch);
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isAuthenticated && cartItems?.length > 0 && !isLoading) {
      syncGuestCartToBackend();
    }
  }, [isAuthenticated, cartItems, isLoading]);

  const { localBrands, loading: brandsLoading } = useFetchBrands({
    skip: !fetchFlags.brands,
  });
  const { localCategories, loading: categoriesLoading } = useFetchCategories({
    skip: !fetchFlags.categories,
  });

  const {
    products: productInfos,
    loadPage,
    productsForDisplay,
  } = useCustomPaginatedProducts({
    skip: !fetchFlags.products,
    setProductsLoading,
    dispatch,
    setEnrichedProductList,
  });

  const brands = useMemo(
    () => (brandsRedux.length > 0 ? brandsRedux : localBrands),
    [brandsRedux, localBrands]
  );

  const categories = useMemo(
    () => (categoriesRedux.length > 0 ? categoriesRedux : localCategories),
    [categoriesRedux, localCategories]
  );

  const { combineProducts } = useCombineProducts({
    productsForDisplay,
    productInfos,
    brands,
    categories,
    setProductsForDisplay,
    dispatch,
  });

  const memoizedCombineProducts = useCallback(() => {
    combineProducts();
    //eslint-disable-next-line
  }, [combineProducts, productInfos, productsForDisplay, brands, categories]);

  useEffect(() => {
    if (fetchFlags.products) {
      loadPage();
    }
  }, [fetchFlags.products, loadPage]);

  useEffect(() => {
    const allLoaded = !productsLoading && !brandsLoading && !categoriesLoading;

    if (allLoaded && fetchFlags.products) {
      setFetchFlags((prev) => ({
        ...prev,
        brands: false,
        categories: false,
        products: false,
      }));

      dispatch(setProductInfos(productInfos || []));
      memoizedCombineProducts();
    }
    //eslint-disable-next-line
  }, [productsLoading, brandsLoading, categoriesLoading]);

  // const { data: brands } = useQuery(
  //   fetchAllBrandsQueryOptions(
  //     {},
  //     {
  //       enabled: true,
  //     }
  //   )
  // );

  // const { data: categories } = useQuery(
  //   fetchAllCategoriesQueryOptions(
  //     {},
  //     {
  //       enabled: true,
  //       staleTime: 1000 * 60 * 5,
  //     }
  //   )
  // );

  // const { data } = useInfiniteQuery(
  //   fetchCustomPaginatedProductsQueryOptions({}, 10, {
  //     staleTime: 1000 * 60 * 5,
  //     enabled: true,
  //   })
  // );

  return (
    <StripeProvider>
      <div className="App">
        <SidebarProvider>
          <div className="flex w-full">
            <MobileSidebar className="hidden shrink-0" />
            <div className="flex-1 flex flex-col min-h-screen w-full">
              <Header />
              <main className="flex-grow bg-white">
                <Toaster />
                <ScrollToTop />
                <Outlet />
              </main>
              <Footer />
            </div>
          </div>
        </SidebarProvider>
      </div>
    </StripeProvider>
  );
};

export default App;
